import React from 'react';

import { Select } from '@components/shared/select/Select';
import { cn } from '@utils/index';

import {
  useProfileNavigation,
  ProfilePage,
} from '../contexts/ProfileNavigationContext';

const InlineIcon = React.memo(function InlineIcon({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [svgContent, setSvgContent] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    if (src && src.toLowerCase().endsWith('.svg')) {
      fetch(src)
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error('Failed to load SVG'))))
        .then((text) => {
          if (!isMounted) return;
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'image/svg+xml');
            const svg = doc.documentElement;
            const existingClass = svg.getAttribute('class') || '';
            const mergedClass = `w-full h-full ${existingClass}`.trim();
            svg.setAttribute('class', mergedClass);
            const serialized = new XMLSerializer().serializeToString(svg);
            setSvgContent(serialized);
          } catch {
            setSvgContent(text);
          }
        })
        .catch(() => {
          if (isMounted) setSvgContent(null);
        });
    } else {
      setSvgContent(null);
    }
    return () => {
      isMounted = false;
    };
  }, [src]);

  if (svgContent) {
    return (
      <span
        className={className}
        role="img"
        aria-label={alt}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    );
  }

  return null;
});

export const NavigationButtons = () => {
  const { activePage, setActivePage } = useProfileNavigation();

  const navigationItems = [
    {
      label: 'Home' as ProfilePage,
      icon: '/icons/home-icn.svg',
      alt: 'home-icon',
      class: 'w-[61px] bottom-0 max-4xl:bottom-0 right-0 -rotate-[15deg]'
    },
    {
      label: 'Profile' as ProfilePage,
      icon: '/icons/profile.svg',
      alt: 'profile-icon',
      class: 'w-[52px] bottom-0 max-4xl:bottom-0 right-[9px] -rotate-[15deg]'
    },
    {
      label: 'Your plans' as ProfilePage,
      icon: '/icons/plans.svg',
      alt: 'plans-icon',
      class: 'w-[67px] bottom-[-25px] max-4xl:bottom-[-25px] right-0 -rotate-[15deg]'
    },
    {
      label: 'Billing' as ProfilePage,
      icon: '/icons/billing.svg',
      alt: 'billing-icon',
      class: 'w-[74px] bottom-[-10px] right-[15px] -rotate-[15deg]'
    },
    // {
    //   label: 'Resources' as ProfilePage,
    //   icon: '/icons/resourses.svg',
    //   alt: 'resources-icon',
    //   class: 'w-[67px] bottom-[-22px] max-4xl:bottom-[-15px] right-[14px] -rotate-[15deg]'
    // },
  ];

  const handleButtonClick = (label: ProfilePage) => {
    setActivePage(label);
  };

  const handleSelectChange = (value: string) => {
    setActivePage(value as ProfilePage);
  };

  const selectItems = navigationItems.map((item) => ({
    value: item.label,
    label: item.label,
  }));

  return (
    <>
      <section className="hidden xl:flex flex-row justify-between gap-10 mt-3 max-4xl:gap-4">
        {navigationItems.map((item, index) => (
          <button
            key={item.label}
            onClick={() => handleButtonClick(item.label)}
            className={`w-83 relative overflow-hidden flex gap-10 text-lg font-bold uppercase tracking-wider py-4 px-7.5 max-1xl:py-3 rounded-full transition-all duration-200 ${
              activePage === item.label
                ? 'bg-primary text-white navigation-button--active'
                : 'bg-white text-profile-nav-text hover:bg-primary hover:text-white navigation-button--inactive'
            }`}
          >
            {item.label}
            <InlineIcon
              src={item.icon}
              alt={item.alt}
              className={cn('absolute portal-nav-btn', item.class)}
            />
          </button>
        ))}
      </section>

      <section className="block xl:hidden w-full">
        <Select
          value={activePage}
          onValueChange={handleSelectChange}
          items={selectItems}
          placeholder="Select page"
          className="w-full text-xl"
        />
      </section>
    </>
  );
};
